import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { currencyFormat, calculateNI, calculateDirectorNic, calculateCompanyDirNic } from "./nationalInsurance"
import { employeeRates, employerData, directorRatesByCategory, AnnualDirectorDataByCategory, AnnualDirectorData, RatesType } from "./variables"

interface OutputTableProps {
  director: boolean;
  pay: number;
  category: string;
  calculationType: string;
  proRata: number;
  firstPeriodPaid: number;
  selectedTaxYear: string;
  comparisonTaxYear: string | null;
}

const formatDifference = (num: number): string => {
  const absNum = Math.abs(num);
  const formattedNum = absNum.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  return num < 0 ? "-" + formattedNum : "+" + formattedNum;
};

export const OutputTable = ({
  director,
  pay,
  category,
  calculationType,
  proRata,
  firstPeriodPaid,
  selectedTaxYear,
  comparisonTaxYear
}: OutputTableProps): JSX.Element => {
  const rows: Array<any> = []
  const comparisonRows: Array<any> = []
  const rates = directorRatesByCategory[category as keyof AnnualDirectorDataByCategory] as AnnualDirectorData
  const months = getMonthsForTaxYear(selectedTaxYear)

  if(!director) {
    for (let i=0; i<months.length-1; i++) {       
      rows.push({
        "employee": calculateNI(
          pay,
          category,
          employeeRates,
          false,
          months[i],
          selectedTaxYear
        ), 
        "employer": calculateNI(
          pay,
          category,
          employerData,
          true,
          months[i],
          selectedTaxYear
        )
      })
      
      if (comparisonTaxYear) {
        comparisonRows.push({
          "employee": calculateNI(
            pay,
            category,
            employeeRates,
            false,
            months[i],
            comparisonTaxYear
          ),
          "employer": calculateNI(
            pay,
            category,
            employerData,
            true,
            months[i],
            comparisonTaxYear
          )
        })
      }
    }
  } else {
    if (calculationType === "standard") {
      for (let i=0; i<months.length-1; i++) {   
        const dir = calculateDirectorNic(
          pay*(Math.max(i+1-firstPeriodPaid,0)),
          rates,
          i<=6?"first_period":"second_period",
          i>0?rows.reduce((a,b)=>a+b['employee'],0):0,
          proRata
        )
        const emp = calculateCompanyDirNic(
          pay*(Math.max(i+1-firstPeriodPaid,0)),
          rates,
          i<=6?"first_period":"second_period",
          i>0?rows.reduce((a,b)=>a+b['employer'],0):0,
          proRata
        )    
        rows.push({"employee":dir, "employer":emp})
      }
    } else {
      for (let i=0; i<months.length-2; i++) {  
        if (i<firstPeriodPaid) {
          rows.push({"employee":0,"employer":0})
        } else {
          rows.push({
            "employee": calculateNI(pay, category, employeeRates, false, months[i], selectedTaxYear),
            "employer": calculateNI(pay, category, employerData, false, months[i], selectedTaxYear)
          })
        }
      }
      let totDir = 0;
      let totComp = 0;
      for (let i=0; i<months.length-1; i++) {   
        totComp += calculateCompanyDirNic(
          pay*(Math.max(i+1-firstPeriodPaid,0)),
          rates,
          i<=6?"first_period":"second_period",
          i>0?totComp:0,
          proRata
        )  
        
        totDir += calculateDirectorNic(
          pay*(Math.max(i+1-firstPeriodPaid,0)),
          rates,
          i<=6?"first_period":"second_period",
          i>0?totDir:0,
          proRata
        )
      }
      rows.push({
        "employee": totDir-rows.reduce((a,b)=>a+b['employee'],0),
        "employer": totComp-rows.reduce((a,b)=>a+b['employer'],0)
      })
    }
  }

  rows.push({
    "employee": Math.abs(rows.reduce((a,b)=>a+b["employee"],0)),
    "employer": Math.abs(rows.reduce((a,b)=>a+b["employer"],0))
  })

  if (comparisonTaxYear && !director) {
    const comparisonTotal = {
      "employee": Math.abs(comparisonRows.reduce((a,b)=>a+b["employee"],0)),
      "employer": Math.abs(comparisonRows.reduce((a,b)=>a+b["employer"],0))
    }
    comparisonRows.push(comparisonTotal)
  }

  return (
    <TableContainer component={Paper} style={{width:"99%",marginLeft:"0px"}}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{fontWeight:"bold",fontSize:"small"}} align="left">Month</TableCell> 
            <TableCell style={{fontWeight:"bold",fontSize:"x-small"}} align="left">
              {director ? "Salary Deductions" : "Salary Deductions"}
              {comparisonTaxYear && !director && " (with difference)"}
            </TableCell>
            <TableCell style={{fontWeight:"bold",fontSize:"x-small"}} align="left">
              {"Employer's NIC"}
              {comparisonTaxYear && !director && " (with difference)"}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ind) => {
            const employeeDiff = comparisonTaxYear && !director ? row.employee - comparisonRows[ind].employee : 0
            const employerDiff = comparisonTaxYear && !director ? row.employer - comparisonRows[ind].employer : 0
            
            return (
              <TableRow 
                key={ind}
                style={{width:'70%'}}
                sx={{ '&:last-child td, &:last-child th': { border: 0, backgroundColor:"#D3D3D3", fontWeight:"bold", fontSize:"small" } }}
              >
                <TableCell style={{width:'20%', fontSize:ind===12?"normal": "small"}} align="left">
                  {months[ind]}
                </TableCell>
                <TableCell style={{width:'40%', fontSize:ind===12?"normal": "small"}} align="left">
                  {currencyFormat(row.employee)}
                  {comparisonTaxYear && !director && employeeDiff !== 0 && (
                    <span style={{
                      marginLeft: '10px',
                      color: employeeDiff > 0 ? 'red' : employeeDiff < 0 ? 'green' : 'inherit'
                    }}>
                      {formatDifference(employeeDiff)}
                    </span>
                  )}
                </TableCell>
                <TableCell style={{width:'40%', fontSize:ind===12?"normal": "small"}} align="left">
                  {currencyFormat(row.employer)}
                  {comparisonTaxYear && !director && employerDiff !== 0 && (
                    <span style={{
                      marginLeft: '10px',
                      color: employerDiff > 0 ? 'red' : employerDiff < 0 ? 'green' : 'inherit'
                    }}>
                      {formatDifference(employerDiff)}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const getMonthsForTaxYear = (taxYear: string): string[] => {
  const yearMap = {
    'firstPeriod': ['Apr-21', 'May-21', 'Jun-21', 'Jul-21', 'Aug-21', 'Sep-21', 'Oct-21', 'Nov-21', 'Dec-21', 'Jan-22', 'Feb-22', 'Mar-22', 'Total'],
    'secondPeriod': ['Apr-22', 'May-22', 'Jun-22', 'Jul-22', 'Aug-22', 'Sep-22', 'Oct-22', 'Nov-22', 'Dec-22', 'Jan-23', 'Feb-23', 'Mar-23', 'Total'],
    'thirdPeriod': ['Apr-23', 'May-23', 'Jun-23', 'Jul-23', 'Aug-23', 'Sep-23', 'Oct-23', 'Nov-23', 'Dec-23', 'Jan-24', 'Feb-24', 'Mar-24', 'Total'],
    'fourthPeriod': ['Apr-24', 'May-24', 'Jun-24', 'Jul-24', 'Aug-24', 'Sep-24', 'Oct-24', 'Nov-24', 'Dec-24', 'Jan-25', 'Feb-25', 'Mar-25', 'Total'],
    'fifthPeriod': ['Apr-25', 'May-25', 'Jun-25', 'Jul-25', 'Aug-25', 'Sep-25', 'Oct-25', 'Nov-25', 'Dec-25', 'Jan-26', 'Feb-26', 'Mar-26', 'Total'],
    'sixthPeriod': ['Apr-26', 'May-26', 'Jun-26', 'Jul-26', 'Aug-26', 'Sep-26', 'Oct-26', 'Nov-26', 'Dec-26', 'Jan-27', 'Feb-27', 'Mar-27', 'Total']
  };
  return yearMap[taxYear as keyof typeof yearMap] || yearMap.sixthPeriod;
};
